// ============================================================
// Projects · Index — the real Projects index surface.
//
// Replaces the old "Domains" placeholder lanes. Reuses the
// existing useProjects() hook (demo + live via sq_projects)
// and the existing ProjectRow component (now with an onSelect
// override that navigates into the per-project page).
//
// Surface anatomy:
//   editorial header + "New Project" button
//   search input (name + client name)
//   status filter chips
//   project list (ProjectRow with navigate onSelect)
// ============================================================

import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import ProjectRow from '../../components/ProjectRow'
import EmptyState from '../../components/EmptyState'
import { useProjects } from '../../hooks/useProjects'
import type { Project } from '../../hooks/types'
import { SHELL_TOKENS, text } from '../../shell/styles'
import NewProjectModal from './NewProjectModal'

const STATUS_FILTERS: { id: string; label: string }[] = [
  { id: 'all',      label: 'All' },
  { id: 'active',   label: 'Active' },
  { id: 'paused',   label: 'Paused' },
  { id: 'archived', label: 'Archived' },
]

export default function ProjectsIndex() {
  const navigate = useNavigate()
  const location = useLocation()
  const { projects, loading, error, refresh } = useProjects()
  const [query, setQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [showNew, setShowNew] = useState(location.pathname.endsWith('/projects/new'))

  // Keep modal state in sync with the URL — entering /projects/new
  // from any link opens the modal; closing it returns to /projects.
  useEffect(() => {
    if (location.pathname.endsWith('/projects/new')) setShowNew(true)
  }, [location.pathname])

  const filtered = useMemo(() => {
    let list = projects
    if (statusFilter !== 'all') {
      list = list.filter((p) => (p.status || '').toLowerCase() === statusFilter)
    }
    if (query.trim()) {
      const q = query.trim().toLowerCase()
      list = list.filter(
        (p) =>
          (p.name || '').toLowerCase().includes(q) ||
          (p.client_name || '').toLowerCase().includes(q) ||
          (p.domain || '').toLowerCase().includes(q),
      )
    }
    return list
  }, [projects, query, statusFilter])

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      {/* Editorial hero */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          marginBottom: 22,
          gap: 16,
        }}
      >
        <div>
          <div
            style={{
              ...text.kicker,
              fontSize: 10,
              letterSpacing: '0.28em',
              color: SHELL_TOKENS.inkFaint,
              marginBottom: 6,
            }}
          >
            SQUARE CODE · PROJECTS
          </div>
          <div style={text.editorial}>All Projects</div>
          <div
            // Announce only the loading state — the count line changes on
            // every search keystroke and would spam screen readers as a
            // permanent live region.
            {...(loading ? { role: 'status' } : {})}
            style={{
              fontSize: 11,
              color: SHELL_TOKENS.inkMuted,
              marginTop: 6,
              fontFamily: 'var(--font-mono)',
            }}
          >
            {loading ? 'loading…' : `${projects.length} total · ${filtered.length} shown`}
          </div>
        </div>

        <button
          type="button"
          onClick={() => setShowNew(true)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 16px',
            borderRadius: 10,
            border: `1px solid ${SHELL_TOKENS.brandLine}`,
            background: SHELL_TOKENS.brandSoft,
            color: SHELL_TOKENS.brand,
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            fontFamily: 'var(--font-mono)',
          }}
        >
          + New Project
        </button>
      </div>

      {/* Search + filters */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: 18,
        }}
      >
        <input
          type="search"
          placeholder="Search projects…"
          aria-label="Search projects"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{
            flex: 1,
            padding: '10px 14px',
            borderRadius: 10,
            border: `1px solid ${SHELL_TOKENS.border}`,
            background: SHELL_TOKENS.surface,
            color: SHELL_TOKENS.ink,
            fontSize: 13,
            fontFamily: 'inherit',
          }}
        />
        <div style={{ display: 'flex', gap: 4 }}>
          {STATUS_FILTERS.map((f) => {
            const active = statusFilter === f.id
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => setStatusFilter(f.id)}
                style={{
                  padding: '8px 12px',
                  borderRadius: 8,
                  border: active
                    ? `1px solid ${SHELL_TOKENS.brandLine}`
                    : `1px solid ${SHELL_TOKENS.border}`,
                  background: active ? SHELL_TOKENS.brandSoft : SHELL_TOKENS.surface,
                  color: active ? SHELL_TOKENS.brand : SHELL_TOKENS.inkDim,
                  cursor: 'pointer',
                  fontSize: 11,
                  fontWeight: active ? 700 : 500,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  fontFamily: 'var(--font-mono)',
                }}
              >
                {f.label}
              </button>
            )
          })}
        </div>
      </div>

      {/* Project list */}
      {loading ? (
        <EmptyState title="Loading projects" body="Pulling portfolio into the shell." />
      ) : error ? (
        <EmptyState
          title="Couldn't load projects"
          body={`The Projects API returned an error: ${error}. Check that the sq_projects migration has been applied and that you're authenticated.`}
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          title={projects.length === 0 ? 'No projects yet' : 'No matching projects'}
          body={
            projects.length === 0
              ? 'Click + New Project to start your first one.'
              : 'Adjust your search or status filter.'
          }
        />
      ) : (
        filtered.map((p: Project) => (
          <ProjectRow
            key={p.id}
            project={p}
            onSelect={(proj) => navigate(`/square/projects/${proj.id}`)}
          />
        ))
      )}

      {showNew && (
        <NewProjectModal
          onClose={() => {
            setShowNew(false)
            if (location.pathname.endsWith('/projects/new')) {
              navigate('/square/projects', { replace: true })
            }
          }}
          onCreated={(id) => {
            setShowNew(false)
            void refresh()
            navigate(`/square/projects/${id}`)
          }}
        />
      )}
    </div>
  )
}
